import { useState } from "react";
import { TriangleAlert } from "lucide-react";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { AssetRow } from "@/components/admin/marketing/asset-row";
import { LogoGroup } from "@/components/admin/marketing/logo-grid";
import { SegmentedFilter } from "@/components/admin/segmented-filter";
import { useAdminView } from "@shared/client/admin-view";
import { usePageTitle } from "@shared/client/view";
import { AdminEmpty, AdminMeta, AdminPanel, StatCard, StatGroup } from "@shared/components/blocks";
import { Alert, AlertDescription, AlertTitle } from "@shared/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import {
  MARKETING_SECTION_LABELS,
  MARKETING_TYPES,
  type MarketingFilterKey,
  type MarketingView,
} from "@shared/lib/marketing-shared";

const META = "Vidéos, carrousels, logos et documents de la marque : aperçu, fichiers et texte des posts";

export default function AdminMarketingPage() {
  usePageTitle("Marketing — Administration Hune");
  const { data, error } = useAdminView<MarketingView>("/api/views/admin/marketing");
  const [filter, setFilter] = useState<MarketingFilterKey>("all");

  if (!data) {
    return error ? (
      <>
        <AdminMeta>{META}</AdminMeta>
        <AdminDataNotice error={error} />
      </>
    ) : null;
  }

  const { stats, problems } = data;
  // Le filtre en cours peut disparaître si le catalogue change.
  const active = data.filters.some((item) => item.key === filter) ? filter : "all";
  const sections = MARKETING_TYPES.filter((type) => active === "all" || active === type)
    .map((type) => ({ type, items: data.items.filter((item) => item.type === type) }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      <AdminMeta note={`marketing/ · ${stats.weight}`}>{META}</AdminMeta>

      <StatGroup className="mb-4.5" columns={4}>
        <StatCard label="Contenus" note={stats.itemsNote} value={stats.items} />
        <StatCard label="Prêts à publier" note={stats.readyNote} value={stats.ready} />
        <StatCard label="Publiés" note={stats.publishedNote} value={stats.published} />
        <StatCard label="Poids de marketing/" note={stats.weightNote} tint={stats.overBudget ? "rose" : undefined} value={stats.weight} />
      </StatGroup>

      {problems.length > 0 && (
        <Alert className="mb-4.5" variant="destructive">
          <TriangleAlert />
          <AlertTitle>{problems.length > 1 ? `${problems.length} contenus ignorés` : "1 contenu ignoré"}</AlertTitle>
          <AlertDescription>
            <p>
              Corrigez <code className="font-mono">marketing/catalog.json</code> : le test du dépôt exige zéro problème.
            </p>
            <ul className="m-0 grid gap-1 pl-4 text-[13px] [overflow-wrap:anywhere]">
              {problems.map((problem) => (
                <li className="list-disc" key={problem}>
                  {problem}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {data.items.length === 0 ? (
        <AdminPanel>
          <AdminEmpty>
            Aucun contenu : ajoutez les fichiers dans <code className="font-mono">marketing/</code> et leur entrée dans{" "}
            <code className="font-mono">marketing/catalog.json</code> (voir <code className="font-mono">marketing/README.md</code>).
          </AdminEmpty>
        </AdminPanel>
      ) : (
        <>
          {/* Un filtre n'a de sens qu'à partir de deux types. */}
          {data.filters.length > 2 && (
            <div className="mb-4.5">
              {/* Sous sm, six segments ne tiennent pas : un Select montre tous les types, sans défilement caché. */}
              <div className="max-sm:hidden">
                <SegmentedFilter
                  items={data.filters}
                  label="Filtrer par type de contenu"
                  onChange={(key) => setFilter(key as MarketingFilterKey)}
                  value={active}
                />
              </div>
              <Select onValueChange={(key) => setFilter(key as MarketingFilterKey)} value={active}>
                <SelectTrigger aria-label="Filtrer par type de contenu" className="w-full text-[13px] sm:hidden">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data.filters.map((item) => (
                    <SelectItem key={item.key} value={item.key}>
                      {item.label} <span className="tabular-nums text-subtle-foreground">{item.count}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-4.5">
            {sections.map(({ type, items }) => (
              <AdminPanel flush key={type} title={MARKETING_SECTION_LABELS[type]}>
                <div className="divide-y">
                  {items.map((item) =>
                    type === "logo" ? <LogoGroup item={item} key={item.id} /> : <AssetRow item={item} key={item.id} />,
                  )}
                </div>
              </AdminPanel>
            ))}
          </div>
        </>
      )}
    </>
  );
}
